import * as React from 'react';
import ErrorImage from './ErrorImage';
import { setupLogger } from '@zoopla/logger';
import {
    heroContentStyle,
    heroContentWrapperStyle,
    heroGridStyle,
    heroImageWrapperStyle,
    heroWrapperStyle,
    imgStyle
} from './ErrorBoundary.styles.css';
import { Box } from '@zoopla-eng/zds-components/box';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';

type Props = {
    children: React.ReactNode;
    renderOnError?: React.ReactNode | null;
};

type State = {
    hasError: boolean;
};

/*
You should use an ErrorBoundary for any non-critical element on the page where you
believe an error shouldn't affect the page in its entirety.
We have a top level ErrorBoundary from the app as a safety net however that will
completely replace the page for the user for any render errors that occur and thus
a worse experience than a single widget/component even below the fold
 */
class ErrorBoundary extends React.Component<Props, State> {
    override state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // TODO use pino and Sean's client side O11y implementation to
        // send a beacon to Cloudwatch
        // https://getpino.io/#/docs/transports
        const logger = setupLogger({
            component: 'global-error-boundary',
            custodian: 'platform'
        });

        logger.error({ err: errorInfo }, error.message);
    }

    override render() {
        const hasDefinedOrNullRenderOnError =
            this.props.renderOnError || this.props.renderOnError === null;

        if (this.state.hasError) {
            return hasDefinedOrNullRenderOnError ? (
                this.props.renderOnError
            ) : (
                <section className={heroWrapperStyle}>
                    <div className={heroGridStyle}>
                        <div className={heroImageWrapperStyle}>
                            <ErrorImage className={imgStyle} />
                        </div>
                        <div className={heroContentStyle}>
                            <div className={heroContentWrapperStyle}>
                                <Box pb="S400">
                                    <h1 className={fontGroups.teapot}>
                                        Something went wrong
                                    </h1>
                                </Box>
                                <p>
                                    Our team has been made aware and are working
                                    to find a solution to this problem. Please
                                    refresh your page or, if the issue
                                    continues, please try again later.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            );
        }
        return this.props.children;
    }
}
export default ErrorBoundary;
